import { ClientSideSuspense } from '@liveblocks/react';
import LiveblocksProvider from '@liveblocks/yjs';
import { LoaderXs } from '@sketchnote/shared-components';
import { withYjs, YjsEditor } from '@slate-yjs/core';
import { usePlateEditorRef } from '@udecode/plate';
import cn from 'classnames';
import { isEmpty } from 'lodash';
import * as React from 'react';
import { useIdleTimer } from 'react-idle-timer';
import { Editor, Transforms } from 'slate';
import { ReactEditor, withReact } from 'slate-react';
import { BlockEditor } from 'src/components/BlockEditor';
import { PageErrorBoundary } from 'src/components/error-boundary/page-error-boundary';
import { useAuth } from 'src/shared/hooks/use-auth';
import { getFormattedValue } from 'src/utils/getFormattedValue';
import { v4 as uuid } from 'uuid';
import * as Y from 'yjs';

import { useScrollToBlock } from '../hooks/useScrollToBlock';
import { CursorOverlayContainer, stringToColour } from './cursor/CursorOverlayContainer';
import { cursorStore } from './cursor/cursorStore';
import { EditorProvider } from './EditorProvider';
import useCollaborativeEditorStore from './liveblocks/collaborativeEditorStore';
import { RoomProvider, useRoom, useStorage } from './liveblocks/config';

const { useEffect, useState, useMemo, useRef } = React;

const emptyNode = { type: 'p', children: [{ text: '' }] };

const DocLoader = () => (
  <div className="h-full w-full flex items-center justify-center m-auto">
    <div className="overflow-hidden">
      <LoaderXs className="mx-auto text-xss" />
    </div>
  </div>
);

export interface CollaborativeEditorProps {
  lesson: string;
  creationId: string;
  onChangeMicroTasks: (arg: { total: number; completed: number }) => void;
  document: any[];
  readOnly?: boolean;
  title: string;
  token: string;
  setEditor?: any;
}

export const CollaborativeEditorBase = ({
  creationId,
  setEditor,
  document,
}: CollaborativeEditorProps): JSX.Element => {
  const [connected, setConnected] = useState(false);
  const [sharedType, setSharedType] = useState<Y.XmlText>();
  const [provider, setProvider] = useState<any>();
  const room = useRoom();
  const storage = useStorage((root) => root);
  const editorT = usePlateEditorRef(creationId)!;

  useEffect(() => {
    const yDoc = new Y.Doc();
    const yProvider = new LiveblocksProvider(room, yDoc);
    const sharedDoc = yDoc.get('slate', Y.XmlText) as Y.XmlText;
    yProvider.on('sync', setConnected);

    setSharedType(sharedDoc);
    setProvider(yProvider);

    return () => {
      yDoc?.destroy();
      yProvider?.off('sync', setConnected);
      yProvider?.destroy();
    };
  }, [room]);

  const initialValue = useMemo(() => {
    if (storage.docs) {
      if ((storage.docs as any)?.data) {
        return (storage.docs as any).data;
      }
      return storage.docs;
    } else if (document) {
      // Handles templates
      return document;
    }
    return [emptyNode];
  }, [storage]);

  if (!connected || !sharedType || !provider) {
    return (
      <div className="h-full w-full flex items-center justify-center m-auto">
        <div className="overflow-hidden">
          <LoaderXs className="mx-auto text-xss" />
        </div>
      </div>
    );
  }

  return (
    <div className={cn('h-full max-w-864 m-auto ')} id="collaborative-editor-block">
      <PageErrorBoundary wrapperClass="mt-32">
        <SlateEditor
          id={creationId}
          sharedType={sharedType}
          localEditor={editorT}
          value={initialValue}
        />
      </PageErrorBoundary>
    </div>
  );
};

export const CollaborativeEditor = (props: CollaborativeEditorProps) => {
  const [editorPlate, setEditorPlate] = useState<ReactEditor>();
  const [connected, setConnected] = useState<boolean>(true);

  const onIdle = () => {
    setConnected(false);
  };

  const onActive = () => {
    setConnected(true);
  };

  useIdleTimer({
    onIdle,
    onActive,
    timeout: 60e4,
    throttle: 500,
  });

  if (!connected)
    return (
      <div className="h-full w-full flex items-center justify-center m-auto">
        <div className="overflow-hidden">
          <LoaderXs className="mx-auto text-xss" />
        </div>
      </div>
    );

  return (
    <EditorProvider id={props.creationId} editor={editorPlate}>
      <RoomProvider
        id={props.creationId}
        initialPresence={{}}
        // unstable_batchedUpdates={unstable_batchedUpdates}
      >
        <ClientSideSuspense fallback={DocLoader}>
          {() => <CollaborativeEditorBase {...props} setEditor={setEditorPlate} />}
        </ClientSideSuspense>
      </RoomProvider>
    </EditorProvider>
  );
};

function SlateEditor({
  id,
  sharedType,
  localEditor,
  value,
}: {
  id: string;
  sharedType: Y.XmlText;
  localEditor;
  value: any;
}) {
  const {
    updateDoc,
    presence,
    updateMember,
    setPosition,
    onRestoreVersion,
    setVersionRestorer,
    liveblocks: { enterRoom, leaveRoom, others },
  } = useCollaborativeEditorStore();
  const setPresence = useCollaborativeEditorStore((state) => state.setPresence);
  const userDetails = useAuth((s) => s.userDetails);
  const dynamicCursors = cursorStore.use.cursors();
  const editorRef = useRef(null);

  useEffect(() => {
    enterRoom(id);
    return () => {
      leaveRoom(id);
      updateMember('');
    };
  }, [enterRoom, leaveRoom]);

  const editor = useMemo(() => {
    // const e = withReact(withYjs(createEditor(), sharedType));
    // const e = withReact(withYjs(createPlateEditor(), sharedType));
    const e = withReact(withYjs(localEditor, sharedType));

    // Ensure editor always has at least 1 valid child
    const { normalizeNode } = e;
    e.normalizeNode = (entry) => {
      const [node] = entry;

      if (!Editor.isEditor(node) || node.children.length > 0) {
        // Make sure all nodes have ids
        if (node.type && !node.id) {
          const newId = uuid();
          node.id = newId;
        }
        return normalizeNode(entry);
      }

      Transforms.insertNodes(editor, emptyNode, { at: [0] });
    };

    return e;
  }, []);

  useEffect(() => {
    if (!onRestoreVersion && editor) {
      const versionRestorer = (version) => {
        Transforms.delete(editor, {
          at: {
            anchor: Editor.start(editor, []),
            focus: Editor.end(editor, []),
          },
        });
        Transforms.insertNodes(editor, version, { at: [0] });
      };
      setVersionRestorer(versionRestorer);
    }
  }, [editor, onRestoreVersion]);

  useEffect(() => {
    YjsEditor.connect(editor);
    return () => YjsEditor.disconnect(editor);
  }, [editor]);

  useEffect(() => {
    setPresence({
      ...presence,
      member: {
        avatar: userDetails?.avatarUrl || '',
        email: userDetails?.email || '',
        firstname: userDetails?.memberInformation?.firstname || '',
        id: userDetails?.memberInformation?.memberId || '',
        lastname: userDetails?.memberInformation?.lastname || '',
      },
    });
    updateMember(userDetails?.memberInformation?.memberId || '');
  }, [userDetails]);

  useEffect(() => {
    // Check if storage has prior document value
    // if storage exists insert it into the editor\
    // only do the transform if no collaborators are present
    // else duplication happens
    if (
      value.length > 0 &&
      editor.children.length === 1 &&
      getFormattedValue(editor.children) === '' &&
      getFormattedValue(editor.children) !== getFormattedValue(value) &&
      (others.filter((i) => i.presence?.presence) || []).length < 1
    ) {
      Transforms.insertNodes(editor, value, { at: [0] });
    }
  }, [value]);

  useScrollToBlock(editor);

  useEffect(() => {
    const mappedOthers = others?.map((other) => ({
      data: {
        style: {
          backgroundColor: stringToColour((other.presence?.presence as any)?.member.id || 'red'),
        },
        member: (other.presence?.presence as any)?.member || {},
      },
      selection: other.presence?.position || {},
    }));
    let cursorObjects = {};

    if (mappedOthers.length) {
      mappedOthers
        .filter((i) => !isEmpty(i.selection))
        .forEach((item) => {
          if (item.data.member.id) cursorObjects[item.data.member.id] = item;
        });
      cursorStore.set.state((draft) => {
        draft.cursors = cursorObjects;
      });
    } else {
      cursorObjects = {};
    }
  }, [others]);

  return (
    <div className="relative" ref={editorRef}>
      <BlockEditor
        id={id}
        value={value}
        editor={editor}
        onChange={(e) => {
          updateDoc(e);
          if (editor.selection) {
            setPosition(editor.selection);
          }
          updateMember(userDetails?.memberInformation?.memberId || '');
        }}
      >
        <CursorOverlayContainer
          containerRef={editorRef}
          cursors={dynamicCursors}
          id={userDetails?.memberInformation?.memberId || ''}
        />
      </BlockEditor>
    </div>
  );
}
